import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Card, Spinner, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { SketchPicker } from 'react-color';
import MediaSelector from '../Media/MediaSelector';
import { createSponsersInstance } from '../../../Models/Sponsers';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const SponsersBuilder = () => {
    const [sponsersInstance, setSponsersInstance] = useState(createSponsersInstance());
    const [loading, setLoading] = useState(false);
    const [isMediaSelectorOpen, setIsMediaSelectorOpen] = useState(false);
    const [activeColorPicker, setActiveColorPicker] = useState(null);
    const [currentColor, setCurrentColor] = useState('#ffffff');
    const colorSwatchRefs = useRef({});

    useEffect(() => {
        loadSponsersData();
    }, []);

    const loadSponsersData = async () => {
        try {
            setLoading(true);
            await sponsersInstance.load();
            setSponsersInstance(sponsersInstance);
        } catch (error) {
            toast.error('Failed to load sponsers data');
            console.error('Error loading sponsers:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMediaSelect = (selectedPaths) => {
        if (Array.isArray(selectedPaths)) {
            sponsersInstance.addImages(selectedPaths);
            setSponsersInstance(sponsersInstance);
        }
        setIsMediaSelectorOpen(false);
    };

    const handleRemoveImage = (imageUrl) => {
        const newInstance = createSponsersInstance();
        Object.assign(newInstance, sponsersInstance);
        newInstance.removeImage(imageUrl);
        setSponsersInstance(newInstance);
    };

    const handleTitleChange = (e) => {
        sponsersInstance.setTitle(e.target.value);
        setSponsersInstance(sponsersInstance);
    };

    const handleVisibilityToggle = () => {
        const newInstance = createSponsersInstance();
        Object.assign(newInstance, sponsersInstance);
        newInstance.toggleVisibility();
        setSponsersInstance(newInstance);
    };

    const handleBackgroundColorChange = (color) => {
        setCurrentColor(color.hex);
        
        const newInstance = createSponsersInstance();
        Object.assign(newInstance, sponsersInstance);
        newInstance.setBackgroundColor(color.hex);
        setSponsersInstance(newInstance);
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            await sponsersInstance.save();
            toast.success('Sponsers saved successfully');
        } catch (error) {
            toast.error('Failed to save sponsers');
            console.error('Error saving sponsers:', error);
        } finally {
            setLoading(false);
        }
    };

    const renderColorPicker = (show, color, onChange, onClose, swatchId) => {
        if (!show) return null;
        const swatchElement = colorSwatchRefs.current[swatchId];
        if (!swatchElement) return null;

        const swatchRect = swatchElement.getBoundingClientRect();
        const pickerStyle = {
            position: 'fixed',
            left: `${swatchRect.left + swatchRect.width + 10}px`,
            top: `${swatchRect.top}px`,
            zIndex: 1000
        };

        return (
            <>
                <div 
                    style={{ 
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 999
                    }}
                    onClick={onClose}
                />
                <div style={pickerStyle}>
                    <SketchPicker 
                        color={activeColorPicker?.swatchId === 'backgroundColor' ? currentColor : color} 
                        onChange={onChange} 
                    />
                </div>
            </>
        );
    };

    const renderColorSwatch = (color, onClick, swatchId) => (
        <div 
            ref={el => colorSwatchRefs.current[swatchId] = el}
            style={{
                width: '30px',
                height: '30px',
                backgroundColor: activeColorPicker?.swatchId === swatchId ? currentColor : color,
                border: '1px solid #ccc',
                marginRight: '10px',
                cursor: 'pointer',
                borderRadius: '4px'
            }}
            onClick={() => {
                setCurrentColor(color);
                onClick();
            }}
        />
    );

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    return (
        <Container className="mt-4">
            <h2 className="mb-4">Sponsers Builder</h2>
            
            <Row className="mb-4">
                <Col>
                    <Form.Group>
                        <Form.Label>Sponsers Title</Form.Label>
                        <Form.Control
                            type="text"
                            value={sponsersInstance.getTitle()}
                            onChange={handleTitleChange}
                            placeholder="Enter sponsers title"
                        />
                    </Form.Group>
                </Col>
                <Col xs="auto" className="d-flex align-items-end">
                    <Form.Check
                        type="switch"
                        id="visibility-switch"
                        label="Show Sponsers"
                        checked={sponsersInstance.isVisible()}
                        onChange={handleVisibilityToggle}
                    />
                </Col>
            </Row>

            <Row className="mb-4">
                <Col>
                    <Form.Group>
                        <Form.Label>Background Color</Form.Label>
                        <div className="d-flex align-items-center">
                            {renderColorSwatch(sponsersInstance.getStyle().SponsorsGridSection.backgroundColor, () => 
                                setActiveColorPicker({
                                    show: true,
                                    swatchId: 'backgroundColor'
                                }), 'backgroundColor'
                            )}
                            {renderColorPicker(
                                activeColorPicker?.show && activeColorPicker?.swatchId === 'backgroundColor',
                                sponsersInstance.getStyle().SponsorsGridSection.backgroundColor,
                                handleBackgroundColorChange,
                                () => setActiveColorPicker(null),
                                'backgroundColor'
                            )}
                            <span className="ms-2">{sponsersInstance.getStyle().SponsorsGridSection.backgroundColor}</span>
                        </div>
                    </Form.Group>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col>
                    <Button 
                        variant="success" 
                        onClick={() => setIsMediaSelectorOpen(true)}
                    >
                        Add Images
                    </Button>
                </Col>
            </Row>

            <Row className="g-4">
                {sponsersInstance.getImages().map((imageUrl, index) => (
                    <Col key={index} xs={6} sm={4} md={3} lg={2}>
                        <Card>
                            <Card.Img 
                                variant="top" 
                                src={imageUrl} 
                                style={{ height: '150px', objectFit: 'cover' }}
                            />
                            <Card.Body className="p-2">
                                <Button
                                    variant="danger"
                                    size="sm"
                                    className="w-100"
                                    onClick={() => handleRemoveImage(imageUrl)}
                                >
                                    <FontAwesomeIcon icon={faTrash} />

                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            <div className="mt-4 d-flex justify-content-end">
                <Button 
                    variant="primary" 
                    onClick={handleSave}
                    disabled={loading}
                >
                    {loading ? (
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    ) : (
                        'Save Sponsers'
                    )}
                </Button>
            </div>

            <MediaSelector
                isOpen={isMediaSelectorOpen}
                onClose={() => setIsMediaSelectorOpen(false)}
                onSelect={handleMediaSelect}
                isMultiple={true}
            />
        </Container>
    );
};

export default SponsersBuilder; 